import React, { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Building2, MapPin, Phone } from "lucide-react";

/**
 * Mandatory blocking modal shown to institute_admin users on first login if their
 * institute profile is missing address + institute_type. Cannot be dismissed —
 * the admin must complete the form before using the app. Renders as a fixed
 * overlay on top of any current page.
 */
export default function InstituteProfileGate() {
  const { user, institute, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [instituteType, setInstituteType] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.role !== "institute_admin" || !institute) return;
    const missing = !institute.address || !institute.institute_type;
    setOpen(Boolean(missing));
  }, [user, institute]);

  useEffect(() => {
    if (!open) return;
    http.get("/institute/types").then(r => setTypes(r.data?.types || [])).catch(() => {});
    // Pre-fill if any field is already saved
    setInstituteType(institute?.institute_type || "");
    setAddress(institute?.address || "");
    setCity(institute?.city || "");
    setState(institute?.state || "");
    setPincode(institute?.pincode || "");
    setContactPhone(institute?.contact_phone || "");
  }, [open, institute]);

  if (!open) return null;

  const save = async () => {
    if (!instituteType) return toast.error("Please pick your institute type");
    if (!address.trim()) return toast.error("Address is required");
    if (!city.trim()) return toast.error("City is required");
    setBusy(true);
    try {
      await http.patch("/institutes/me", {
        institute_type: instituteType,
        address: address.trim(),
        city: city.trim(),
        state: state.trim(),
        pincode: pincode.trim(),
        contact_phone: contactPhone.trim(),
      });
      toast.success("Profile saved — welcome aboard!");
      if (refresh) await refresh();
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 overflow-y-auto" data-testid="institute-profile-gate">
      <div className="bg-[#FDFBF7] rounded-2xl p-6 max-w-xl w-full my-6 shadow-2xl">
        <div className="mb-5">
          <div className="label-eyebrow mb-2">One-time setup</div>
          <h2 className="font-display text-2xl tracking-tight mb-1">Tell us about your institute</h2>
          <p className="text-sm text-[#7A7369]">
            We need a few details to comply with Indian DPDP & tax norms and to power your invoices. Takes 30 seconds.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-[#4A4640] mb-2 flex items-center gap-1.5"><Building2 size={13} /> Type of institute *</label>
            <select className="input-field" value={instituteType} onChange={(e) => setInstituteType(e.target.value)} data-testid="profile-type">
              <option value="">— Select one —</option>
              {types.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm text-[#4A4640] mb-2 flex items-center gap-1.5"><MapPin size={13} /> Address *</label>
            <textarea className="input-field" rows="2" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Building, street, area" data-testid="profile-address" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm text-[#4A4640] mb-2">City *</label>
              <input className="input-field" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Mumbai" data-testid="profile-city" />
            </div>
            <div>
              <label className="block text-sm text-[#4A4640] mb-2">State</label>
              <input className="input-field" value={state} onChange={(e) => setState(e.target.value)} placeholder="Maharashtra" data-testid="profile-state" />
            </div>
            <div>
              <label className="block text-sm text-[#4A4640] mb-2">Pincode</label>
              <input className="input-field" value={pincode} onChange={(e) => setPincode(e.target.value)} placeholder="400001" data-testid="profile-pincode" />
            </div>
          </div>

          <div>
            <label className="block text-sm text-[#4A4640] mb-2 flex items-center gap-1.5"><Phone size={13} /> Contact phone (optional)</label>
            <input className="input-field" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="+91 98765 43210" data-testid="profile-phone" />
          </div>

          <button onClick={save} disabled={busy} className="btn-primary w-full" data-testid="profile-save">
            {busy ? "Saving…" : "Save & continue"}
          </button>
          <p className="text-xs text-[#7A7369] text-center">
            This is a one-time prompt. You can edit these details later in <em>Tutor settings</em>.
          </p>
        </div>
      </div>
    </div>
  );
}
